import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowRight, 
  CheckCircle2, 
  XCircle,
  Construction,
  Droplets,
  Factory,
  Building2,
  Layers,
  Home,
  ArrowDownToLine
} from "lucide-react";

const carouselImages = [
  "https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/rgxhq5jz_IMG_crapet1-1-scaled.webp",
  "https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/90ry1kgy_IMG_6546-2-scaled.webp",
  "https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/sgg1l83p_mall2-2%20%281%29.webp",
  "https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/qgtd8uyq_PHOTO-2026-01-28-20-03-44%204.jpg",
  "https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/a9wqf53t_EOTA.png"
];

const probarSpecs = [
  { property: "Density", probar: "2.0 g/cm³", tmt: "7.85 g/cm³" },
  { property: "Tensile Strength", probar: "1000+ MPa", tmt: "500-550 MPa" },
  { property: "Corrosion Resistance", probar: "Excellent", tmt: "Poor" },
  { property: "Electrical Conductivity", probar: "Non-conductive", tmt: "Conductive" },
  { property: "Thermal Conductivity", probar: "Low", tmt: "High" },
  { property: "Design Life", probar: "100+ years", tmt: "30-50 years" },
  { property: "Concrete Cover Required", probar: "15-20mm", tmt: "40-75mm" },
];

const probarApplications = [
  { icon: Construction, name: "Bridges & Flyovers", desc: "Ideal for coastal and high-corrosion zones" },
  { icon: Droplets, name: "Coastal Structures", desc: "Sea walls, ports, marine facilities" },
  { icon: Building2, name: "Tunnels & Metro", desc: "Reduced interference with signals" },
  { icon: Factory, name: "Industrial Facilities", desc: "Chemical plants, water treatment" },
];

const promeshApplications = [
  { icon: Factory, name: "Industrial Floors", desc: "Heavy-duty warehouse & factory floors" },
  { icon: ArrowDownToLine, name: "Pavements", desc: "Roads, runways, parking areas" },
  { icon: Layers, name: "Precast Panels", desc: "Wall panels, cladding systems" },
  { icon: Home, name: "Plaster Reinforcement", desc: "Crack prevention in walls" },
];

export default function ProductsPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="products-page">
      {/* Hero Section with Carousel Background */}
      <section className="relative overflow-hidden" data-testid="products-hero">
        {/* Carousel Background Images */}
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url('${image}')` }}
          />
        ))}
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-slate-900/75"></div>
        
        {/* Content */}
        <div className="section-container py-20 md:py-28 relative z-10">
          <div className="max-w-3xl">
            <div className="accent-line mb-6"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Barlow_Condensed']">
              Our Products
            </h1>
            <p className="text-lg text-slate-200 max-w-2xl">
              Advanced GFRP solutions engineered for superior performance, 
              durability, and cost-efficiency in modern infrastructure.
            </p>
          </div>
          
          {/* Carousel Indicators */}
          <div className="flex gap-2 mt-8">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentImageIndex 
                    ? 'bg-yellow-400 w-8' 
                    : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Products Tabs */}
      <section className="section-spacing bg-slate-50" data-testid="products-tabs">
        <div className="section-container">
          <Tabs defaultValue="probar" className="w-full">
            {/* Product Image Buttons */}
            <TabsList className="w-full max-w-3xl mx-auto grid grid-cols-2 gap-6 mb-12 bg-transparent h-auto">
              <TabsTrigger 
                value="probar" 
                className="rounded-none p-0 bg-white border-2 border-slate-200 hover:border-yellow-400 data-[state=active]:border-yellow-400 data-[state=active]:shadow-lg transition-all h-auto"
                data-testid="tab-probar"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/0m6zygxl_PHOTO-2026-01-28-20-03-45%203.jpg"
                  alt="PROBAR - Professional Rebar"
                  className="w-full h-auto object-contain p-4"
                />
              </TabsTrigger>
              <TabsTrigger 
                value="promesh" 
                className="rounded-none p-0 bg-white border-2 border-slate-200 hover:border-yellow-400 data-[state=active]:border-yellow-400 data-[state=active]:shadow-lg transition-all h-auto"
                data-testid="tab-promesh"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/uo2diriw_PHOTO-2026-01-28-20-03-46.jpg"
                  alt="PROMESH"
                  className="w-full h-auto object-contain p-4"
                />
              </TabsTrigger>
            </TabsList>

            {/* PRO BAR Content */}
            <TabsContent value="probar" className="mt-0" data-testid="probar-content">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-16">
                <div>
                  <span className="text-sm font-mono text-teal-700 uppercase tracking-wider">
                    GFRP Rebar
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mt-2 mb-6">
                    PRO BAR – The Future of Reinforcement
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    PRO BAR is our premium Glass Fiber Reinforced Polymer (GFRP) rebar, engineered 
                    to replace traditional TMT steel in concrete structures. With superior tensile 
                    strength, complete corrosion immunity, and dramatically reduced weight, PRO BAR 
                    delivers infrastructure that lasts over 100 years.
                  </p>
                  
                  <h3 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-xl mb-4">
                    Key Advantages
                  </h3>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Up to 80% lighter than steel TMT bars",
                      "100% corrosion & chemical resistant",
                      "Non-conductive – no electromagnetic interference",
                      "Lower concrete cover requirement (15-20mm vs 40-75mm)",
                      "100+ year design life with zero maintenance",
                      "IIT Hyderabad tested and certified"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/contact">
                    <Button 
                      className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none"
                      data-testid="probar-quote-btn"
                    >
                      Request Quote
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/8s0f513t_PHOTO-2026-01-28-20-03-44.jpg"
                    alt="PRO BAR GFRP Rebar"
                    className="w-full h-[350px] object-cover border border-slate-200 mb-6"
                  />
                  <div className="bg-slate-900 p-6 text-white">
                    <p className="font-mono text-yellow-400 text-sm mb-2">TECHNICAL SPEC</p>
                    <p className="font-['Barlow_Condensed'] text-2xl font-bold">
                      Tensile Strength: 1000+ MPa
                    </p>
                    <p className="text-slate-400 text-sm mt-2">
                      Nearly 2x stronger than conventional TMT steel
                    </p>
                  </div>
                </div>
              </div>

              {/* PRO BAR Comparison Table */}
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6 text-center">
                  PRO BAR vs TMT Steel Comparison
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full comparison-table" data-testid="probar-comparison-table">
                    <thead>
                      <tr>
                        <th className="bg-slate-900 text-white p-4">Property</th>
                        <th className="bg-yellow-400 text-slate-900 p-4">PRO BAR (GFRP)</th>
                        <th className="bg-slate-700 text-white p-4">TMT Steel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {probarSpecs.map((spec, index) => (
                        <tr key={index} className="border-b border-slate-200">
                          <td className="p-4 font-medium text-slate-900">{spec.property}</td>
                          <td className="p-4 text-slate-700 bg-yellow-50">{spec.probar}</td>
                          <td className="p-4 text-slate-600">{spec.tmt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* PRO BAR Applications */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6 text-center">
                  Applications
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {probarApplications.map((app, index) => (
                    <div 
                      key={index}
                      className="bg-white border border-slate-200 p-6 card-hover text-center"
                      data-testid={`probar-app-${index}`}
                    >
                      <div className="w-14 h-14 bg-yellow-400 flex items-center justify-center mx-auto mb-4">
                        <app.icon className="w-7 h-7 text-slate-900" />
                      </div>
                      <h4 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-lg mb-2">
                        {app.name}
                      </h4>
                      <p className="text-slate-600 text-sm">{app.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* PRO MESH Content */}
            <TabsContent value="promesh" className="mt-0" data-testid="promesh-content">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-16">
                <div>
                  <span className="text-sm font-mono text-teal-700 uppercase tracking-wider">
                    GFRP Mesh
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mt-2 mb-6">
                    PRO MESH – Advanced Grid Reinforcement
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    PRO MESH is our high-performance GFRP mesh solution designed for industrial 
                    floors, pavements, wall panels, and plaster reinforcement. It provides superior 
                    crack control while eliminating corrosion concerns entirely.
                  </p>
                  
                  <h3 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-xl mb-4">
                    Key Advantages
                  </h3>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Superior crack control and distribution",
                      "100% rust-proof and chemical resistant",
                      "Lightweight – easy handling and installation",
                      "Reduced installation time and labor costs",
                      "No need for tie wires – self-supported grid",
                      "Excellent for harsh chemical environments"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/contact">
                    <Button 
                      className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none"
                      data-testid="promesh-quote-btn"
                    >
                      Request Quote
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/t85m8kxg_PHOTO-2026-01-28-20-03-44%203.jpg"
                    alt="PRO MESH GFRP Mesh"
                    className="w-full h-[350px] object-cover border border-slate-200 mb-6"
                  />
                  <div className="bg-slate-900 p-6 text-white">
                    <p className="font-mono text-yellow-400 text-sm mb-2">INSTALLATION BENEFIT</p>
                    <p className="font-['Barlow_Condensed'] text-2xl font-bold">
                      50% Faster Installation
                    </p>
                    <p className="text-slate-400 text-sm mt-2">
                      Lightweight design reduces labor and equipment needs
                    </p>
                  </div>
                </div>
              </div>

              {/* PRO MESH Benefits */}
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6 text-center">
                  PRO MESH vs Steel Mesh
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-yellow-50 border border-teal-200 p-6">
                    <h4 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-xl mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                      PRO MESH Advantages
                    </h4>
                    <ul className="space-y-2">
                      {[
                        "Zero corrosion – permanent solution",
                        "80% lighter for easy handling",
                        "Non-conductive and non-magnetic",
                        "Lower total installed cost",
                        "Extended service life (100+ years)",
                        "No protective coatings required"
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-slate-100 border border-slate-200 p-6">
                    <h4 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-xl mb-4 flex items-center gap-2">
                      <XCircle className="w-6 h-6 text-red-500" />
                      Steel Mesh Limitations
                    </h4>
                    <ul className="space-y-2">
                      {[
                        "Prone to rust and corrosion",
                        "Heavy – difficult to transport",
                        "Conducts electricity",
                        "Higher lifecycle cost",
                        "Requires replacement over time",
                        "Needs anti-corrosion treatment"
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* PRO MESH Applications */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6 text-center">
                  Applications
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {promeshApplications.map((app, index) => (
                    <div 
                      key={index}
                      className="bg-white border border-slate-200 p-6 card-hover text-center"
                      data-testid={`promesh-app-${index}`}
                    >
                      <div className="w-14 h-14 bg-yellow-400 flex items-center justify-center mx-auto mb-4">
                        <app.icon className="w-7 h-7 text-slate-900" />
                      </div>
                      <h4 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-lg mb-2">
                        {app.name}
                      </h4>
                      <p className="text-slate-600 text-sm">{app.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Applications Link Section */}
      <section className="section-spacing bg-yellow-400" data-testid="applications-link">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
                Where Can You Use GFRP?
              </h2>
              <p className="text-slate-700 mb-6 leading-relaxed">
                Our PRO BAR and PRO MESH products are trusted across 24+ application areas including 
                highways, bridges, tunnels, water treatment plants, chemical facilities, and more. 
                Discover how GFRP is revolutionizing infrastructure in your industry.
              </p>
              <Link to="/applications">
                <Button 
                  className="bg-slate-900 text-white hover:bg-slate-800 font-bold uppercase tracking-wider rounded-none px-8 py-6"
                  data-testid="products-applications-btn"
                >
                  Explore All Applications
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["Bridges & Flyovers", "Coastal Structures", "Industrial Floors", "Highway Construction", "Water Tanks", "Hospital MRI"].map((app, i) => (
                <div key={i} className="bg-white/80 backdrop-blur p-4 text-center">
                  <p className="text-slate-900 font-bold font-['Barlow_Condensed']">{app}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-slate-900 relative noise-overlay" data-testid="products-cta">
        <div className="section-container relative z-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Barlow_Condensed'] mb-6">
            Need Technical Specifications?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10">
            Our engineering team is ready to provide detailed technical data, 
            project consultation, and custom solutions for your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none px-8 py-6"
                data-testid="products-contact-btn"
              >
                Request Technical Details
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/applications">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-bold uppercase tracking-wider rounded-none px-8 py-6"
                data-testid="products-testing-btn"
              >
                View Applications
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
